// ---------------------------------------------------------------------------
// useSyntheticJob — Polls a synthetic run until it reaches a terminal
// status, then stops. Surfaces { job, isLoading, error, isTerminal } for
// downstream rendering.
//
// The polling interval is fixed at 10 s for now — the API is local, and
// jitter / backoff aren't needed at this scale. Callers that need different
// cadences can wrap this hook.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import type { SyntheticRunDetail } from '../../services/syntheticJob.interface.js';
import { WorkflowApiService } from '../../services/workflowApi.js';

import {
  TERMINAL_STATUSES,
  type UseSyntheticJobResult,
} from './useSyntheticJob.interface.js';

const POLL_INTERVAL_MS = 10_000;

export function useSyntheticJob(
  jobId: string | undefined,
): UseSyntheticJobResult {
  const httpClient = useHttpClient();
  const service = useMemo(
    () => new WorkflowApiService(httpClient),
    [httpClient],
  );

  const [job, setJob] = useState<SyntheticRunDetail | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(jobId !== undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (jobId === undefined) {
      setJob(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    setIsLoading(true);
    setError(null);

    async function fetchOnce(): Promise<void> {
      try {
        const next = await service.getSyntheticRun(jobId!);
        if (cancelled) return;

        setJob(next);
        setError(null);
        setIsLoading(false);

        if (TERMINAL_STATUSES.has(next.runState)) {
          // Stop polling — the job is done (success or failure).
          return;
        }

        timer = setTimeout(() => {
          void fetchOnce();
        }, POLL_INTERVAL_MS);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load job');
        setIsLoading(false);
        // Keep polling on transient errors — the next tick may succeed.
        timer = setTimeout(() => {
          void fetchOnce();
        }, POLL_INTERVAL_MS);
      }
    }

    void fetchOnce();

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [jobId, service]);

  const isTerminal = job !== null && TERMINAL_STATUSES.has(job.runState);

  return { job, isLoading, error, isTerminal };
}
